import { type TriggerProps } from '@atlaskit/popup';
import { type Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { DropIndicator } from '@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/box-without-terminal';
import { useCommentStateContext } from '@databases/comments/hooks/useCommentContext';
import { aiFirstCreationStreamingState } from '@databases/database-ai-first-creation-state';
import { useDatabaseStreamState } from '@databases/database-ai-first-creation-ui';
import { subscribeTo, key } from '@databases/ping-registry';
import { type HoverProps, useHoverState } from '@databases/react';
import { type DefaultRootState, defaultStoreUtils } from '@databases/redux';
import { getService } from '@databases/service-container';
import { TABLE_CLASSNAMES } from '@databases/utils/constants';
import { useIsFeatureEnabled } from '@databases/utils/hooks/useIsFeatureEnabled';
import { selectValueIdFromThreadIdOptional } from '@databases/utils/redux/comments/selectors';
import { getValueId } from '@databases/utils/utils';
import DatabaseValueInlineEdit from '@databases/value/DatabaseValueInlineEdit';
import VisibilityManager from '@databases/visibility-manager';
import composeRefs from '@seznam/compose-react-refs';
import classNames from 'classnames';
import React, { type Ref, useEffect, useMemo, useRef } from 'react';
import { useSnapshot } from 'valtio';

export type TableLayoutCellProps = {
  contentId: string;
  entryId: string;
  fieldId: string;
  rowIndex: number;
  colIndex: number;
  isContentEditable: boolean;
  canEditContent: boolean;
  canEditFields: boolean;
  editingIds: Set<{ entryId: string; fieldId: string | undefined }>;
  setEditingIds: React.Dispatch<
    React.SetStateAction<Set<{ entryId: string; fieldId: string | undefined }>>
  >;

  isFirstInRow: boolean;
  isLastInRow: boolean;
  isLastInColumn: boolean;
  isSelectionDisabled?: boolean;
  isInsertContentDisabled?: boolean;
  showAsDisabled?: boolean;
  addRowBeforeButtonHoverProps: HoverProps;
  addRowAfterButtonHoverProps: HoverProps;
  currentViewId: string;
  dropIndicatorEdge?: Edge | null;
  onIsSelectedChange?: (options?: { isMultiSelectOn?: boolean }) => void;
  handleOnContextMenu?: (event: React.MouseEvent) => void;
  currentSortFieldId?: string;
};

/**
 * During the `databasesSelectionsPerformance`, edits must be kept in parity
 * between TableLayoutCell and LegacyTableLayoutCell.
 */
const TableLayoutCell = React.memo(
  React.forwardRef<HTMLTableSectionElement, TableLayoutCellProps & Partial<TriggerProps>>(
    function TableLayoutCell(
      {
        contentId,
        entryId,
        fieldId,
        rowIndex,
        colIndex,
        isContentEditable,
        canEditContent,
        editingIds,
        setEditingIds,
        isFirstInRow,
        isLastInColumn,
        showAsDisabled,
        onIsSelectedChange,
        currentSortFieldId,
        dropIndicatorEdge,
        ...rest
      },
      ref
    ) {
      const cellRef = useRef<HTMLTableCellElement | null>(null);
      const { focusedCommentThreadId, openThread } = useCommentStateContext();
      const isFirstField = rowIndex === 0;

      useEffect(() => {
        const k = key('cell', { entryId, fieldId });
        const unsubscribe = subscribeTo(k, 'selected', (isSelected: boolean) => {
          if (isSelected && cellRef.current && !openThread.valueId) {
            cellRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'nearest',
            });
          }
        });

        return unsubscribe;
      }, [entryId, fieldId, openThread]);

      const [focusedThreadValueId] = defaultStoreUtils.useEqualSelector(
        (state: DefaultRootState) => {
          const focusedThreadValueId = focusedCommentThreadId
            ? selectValueIdFromThreadIdOptional(state, {
                contentId,
                threadId: focusedCommentThreadId,
              })
            : undefined;

          return [focusedThreadValueId] as const;
        }
      );

      const valueId = useMemo(() => {
        return getValueId(entryId, fieldId);
      }, [entryId, fieldId]);

      const initialIsEditing = useMemo(() => {
        for (const { entryId: editingEntryId, fieldId: editingFieldId } of editingIds) {
          if (
            (isFirstField &&
              editingEntryId === entryId &&
              editingFieldId === undefined) ||
            (editingEntryId === entryId && editingFieldId === fieldId)
          ) {
            return true;
          }
        }

        return false;
      }, [editingIds, entryId, fieldId, isFirstField]);

      useEffect(() => {
        const cellElement = cellRef.current;
        const visibilityManager = VisibilityManager.getInstance();

        if (cellElement && focusedThreadValueId === valueId) {
          visibilityManager.observe(cellElement, (el, unobserve) => {
            if (!el.isIntersecting) {
              // requestAnimationFrame is used to ensure that the scroll happens after the DOM update
              globalThis.requestAnimationFrame(() => {
                el.target.scrollIntoView({
                  behavior: 'instant',
                  block: 'center',
                  inline: 'nearest',
                });
              });

              unobserve(el.target);
            }
          });
        }

        return () => {
          visibilityManager.disconnect();
        };
      }, [focusedThreadValueId, valueId]);

      const [isHoverCell, hoverProps, setIsHoverCell] = useHoverState();

      const { isStreaming, operationType } = useDatabaseStreamState();
      const {
        entryIdSelectedForProcessing,
        isSingleFieldStreaming,
        currentEntryOperation,
      } = useSnapshot(aiFirstCreationStreamingState);
      const isDbAiFirstCreationEnabled = useIsFeatureEnabled('dbAiFirstCreation');
      const isEditProcessing =
        isStreaming &&
        isDbAiFirstCreationEnabled &&
        operationType === 'edit' &&
        entryId === entryIdSelectedForProcessing &&
        currentEntryOperation === 'edit';

      return (
        <td
          {...rest}
          {...hoverProps}
          data-testid={'database-cell'}
          data-field-id={fieldId}
          data-entry-id={entryId}
          data-row-index={rowIndex}
          data-col-index={colIndex}
          ref={composeRefs(cellRef, ref as Ref<HTMLTableCellElement>)}
          onBlur={(e) => {
            const clickedInsideComment =
              e.relatedTarget?.closest('#comment-threads-popup') ||
              e.relatedTarget?.closest('[data-canvas-target="comment-thread-dialog"]') ||
              e.target?.closest('#comment-threads-popup');
            const focusStayingInCell = e.currentTarget.contains(e.relatedTarget);
            if (clickedInsideComment || focusStayingInCell) {
              return;
            }
            const selectionsActions = getService('SelectionsActions');
            if (e.relatedTarget === null) {
              const cell = e.currentTarget;
              requestAnimationFrame(() => {
                if (cell.contains(document.activeElement)) {
                  return;
                }
                selectionsActions.deselectAll();
              });
              return;
            }
            selectionsActions.deselectAll();
          }}
          className={classNames([
            {
              [TABLE_CLASSNAMES.SHOW_AS_DISABLED]: showAsDisabled,
              [TABLE_CLASSNAMES.IS_ROVO_EDIT_PROCESSING]:
                isEditProcessing && !isSingleFieldStreaming,
            },
          ])}
        >
          <DatabaseValueInlineEdit
            contentId={contentId}
            fieldId={fieldId}
            entryId={entryId}
            initialIsEditing={initialIsEditing}
            setEditingIds={setEditingIds}
            isReadonly={!canEditContent}
            isContentEditable={isContentEditable}
            onIsSelectedChange={onIsSelectedChange}
            isFirstInRow={isFirstInRow}
            matchHeaderPadding={true}
            isLastInColumn={isLastInColumn}
            enableSelection
            isHoverCell={isHoverCell}
            setIsHoverCell={setIsHoverCell}
            currentSortFieldId={currentSortFieldId}
          />
          {dropIndicatorEdge && <DropIndicator edge={dropIndicatorEdge} />}
        </td>
      );
    }
  )
);

export default TableLayoutCell;
